// Caixa de texto para dinheiro — padrão do sistema (todo o dashboard).
// Digita da direita para a esquerda, em centavos: "1" → 0,01, "10" → 0,10,
// "100" → 1,00, "1000" → 10,00. Apagar um dígito anda de volta na mesma lógica;
// selecionar tudo e apagar limpa o campo. Separador de milhar "." (pt-BR).
// Pura: sem dependência de domínio — dá para testar `formatarCentavos` direto.

import { el } from "../ui.ts";

export interface CampoMoeda {
  /** O `<span>` que embrulha "R$" + o `<input>`. Vai no formulário. */
  readonly element: HTMLElement;
  readonly input: HTMLInputElement;
  /** Centavos digitados (inteiro ≥ 0) ou `null` se o campo está vazio. */
  centavos(): number | null;
  setCentavos(v: number | null): void;
}

/** `12345` → `"123,45"`; `100000` → `"1.000,00"`. */
export function formatarCentavos(centavos: number): string {
  return (centavos / 100).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function soDigitos(s: string): string {
  return s.replace(/\D/g, "");
}

function lerCentavos(valorBruto: string): number | null {
  if (valorBruto.trim() === "") return null; // campo esvaziado (ex.: Ctrl+A, Del)
  const d = soDigitos(valorBruto);
  return d === "" ? 0 : parseInt(d, 10);
}

export function inputMoeda(opts: {
  centavos?: number | null;
  placeholder?: string;
  ariaLabel?: string;
} = {}): CampoMoeda {
  const input = el("input", {
    type: "text",
    inputmode: "numeric",
    class: "moeda-input",
    placeholder: opts.placeholder ?? "0,00",
    ...(opts.ariaLabel ? { "aria-label": opts.ariaLabel } : {}),
  }) as HTMLInputElement;

  const aplicar = (): void => {
    const c = lerCentavos(input.value);
    input.value = c == null ? "" : formatarCentavos(c);
    // caixa estilo caixa registradora: o cursor fica sempre no fim
    const n = input.value.length;
    input.setSelectionRange(n, n);
  };

  input.addEventListener("input", aplicar);
  // ao focar, joga o cursor para o fim (evita edição no meio, que confunde a máscara)
  input.addEventListener("focus", () => {
    const n = input.value.length;
    setTimeout(() => input.setSelectionRange(n, n), 0);
  });

  const setCentavos = (v: number | null): void => {
    input.value =
      v == null ? "" : formatarCentavos(Math.max(0, Math.round(v)));
  };
  if (opts.centavos !== undefined) setCentavos(opts.centavos);

  return {
    element: el(
      "span",
      { class: "moeda-campo" },
      el("span", { class: "moeda-prefixo" }, "R$"),
      input,
    ),
    input,
    centavos: () => lerCentavos(input.value),
    setCentavos,
  };
}
